'use client'

import { m } from 'framer-motion'
import { BarChart3, Coins, ShieldCheck, Zap } from 'lucide-react'

export default function AgencyWhyUs() {
  return (
    <section className="relative overflow-hidden py-24">
      <div className="home-container relative z-10">
        <div className="mb-16 text-center">
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-400" />
            <span className="home-meta text-gray-400">Преимущества</span>
          </m.div>

          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="home-title text-white"
          >
            Почему Клиенты <span className="text-blue-500">Выбирают Нас</span>
          </m.h2>
        </div>

        <div className="grid h-auto grid-cols-1 grid-rows-2 gap-4 md:h-[600px] md:grid-cols-3 lg:grid-cols-4">
          <m.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            whileHover={{ scale: 0.98 }}
            transition={{ duration: 0.4 }}
            className="group relative row-span-2 flex flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-[#0a0f18]/60 p-8 backdrop-blur-sm transition-colors hover:border-blue-500/30 md:col-span-2"
          >
            <div className="absolute top-0 right-0 h-[400px] w-[400px] translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600/10 blur-[100px] transition-colors duration-500 group-hover:bg-blue-600/20" />

            <div className="relative z-10">
              <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-400">
                <Zap className="h-7 w-7" />
              </div>
              <h3 className="home-card-title mb-4 text-white">Полный цикл</h3>
              <p className="home-subnote max-w-md leading-relaxed text-gray-400">
                Мы закрываем все вопросы digital-присутствия. От разработки брендбука до запуска
                высоконагруженных систем. Вам не нужно искать десять подрядчиков — у нас есть все
                специалисты in-house.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              <span className="rounded-lg border border-white/5 bg-white/5 px-3 py-1 text-xs text-gray-400">
                Dev
              </span>
              <span className="rounded-lg border border-white/5 bg-white/5 px-3 py-1 text-xs text-gray-400">
                Design
              </span>
              <span className="rounded-lg border border-white/5 bg-white/5 px-3 py-1 text-xs text-gray-400">
                Ads
              </span>
              <span className="rounded-lg border border-white/5 bg-white/5 px-3 py-1 text-xs text-gray-400">
                Support
              </span>
            </div>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 0.98 }}
            className="flex h-[280px] flex-col justify-center rounded-3xl border border-white/10 bg-[#0a0f18]/60 p-6 backdrop-blur-sm transition-colors hover:border-green-500/30 md:col-span-1 md:h-auto"
          >
            <ShieldCheck className="mb-4 h-8 w-8 text-green-400" />
            <h3 className="home-card-title-sm mb-2 text-white">Локальность</h3>
            <p className="home-subnote leading-relaxed text-gray-400">
              Всегда на связи в Майкопе. Приедем, обсудим, решим любые вопросы лично.
            </p>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 0.98 }}
            className="flex h-[280px] flex-col justify-center rounded-3xl border border-white/10 bg-[#0a0f18]/60 p-6 backdrop-blur-sm transition-colors hover:border-orange-500/30 md:col-span-1 md:h-auto"
          >
            <Coins className="mb-4 h-8 w-8 text-orange-400" />
            <h3 className="home-card-title-sm mb-2 text-white">Честные цены</h3>
            <p className="home-subnote leading-relaxed text-gray-400">
              Без переплат за «столичный воздух». Только реальная работа по прозрачной смете.
            </p>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 0.98 }}
            className="flex h-auto min-h-[200px] flex-col items-start justify-between rounded-3xl border border-white/10 bg-gradient-to-r from-blue-900/20 to-purple-900/20 p-8 backdrop-blur-sm transition-colors hover:border-purple-500/30 sm:flex-row sm:items-center md:col-span-2 md:h-auto"
          >
            <div className="mb-4 sm:mb-0">
              <h3 className="home-card-title-sm mb-2 flex items-center gap-3 text-white">
                <BarChart3 className="h-6 w-6 text-purple-400" />
                Работаем на KPI
              </h3>
              <p className="home-subnote max-w-xs text-gray-400">
                Рост продаж, заявок и узнаваемости бренда — наши главные метрики.
              </p>
            </div>
            <div className="self-end font-mono text-5xl font-bold text-white/10 sm:self-center sm:text-6xl">
              ROI
            </div>
          </m.div>
        </div>
      </div>
    </section>
  )
}
